import { useQuery, useMutation } from '@apollo/client'
import { ToastContext } from 'contexts'
import { useContext } from 'react'

import createAccountSubnetApiServiceGql from 'gql/subnet-api/createAccountSubnetApiService.gql'
import getAccountSubnetApiServicesGql from 'gql/subnet-api/getAccountSubnetApiServices.gql'
import deleteAccountSubnetApiServiceGql from 'gql/subnet-api/deleteAccountSubnetApiService.gql'
import updateSubnetApiKeyGql from 'gql/subnet-api/updateAccountSubnetApiKey.gql'
/**
 * Creates an account subnet API service.
 *
 * @param {Object} input - The input data for creating the account subnet API service, including the subnet_api_service_id.
 * @return {Object} The created account subnet API service data.
 */
export const useCreateAccountSubnetApiService = () => {
  const { setToast } = useContext(ToastContext)
  const [mutation, { loading }] = useMutation(createAccountSubnetApiServiceGql, {
    update(
      cache,
      {
        data: {
          createAccountSubnetApiService: { account_subnet_api_service },
        },
      },
    ) {
      const queryResponse: any = cache.readQuery({ query: getAccountSubnetApiServicesGql })
      cache.writeQuery({
        query: getAccountSubnetApiServicesGql,
        data: {
          getAccountSubnetApiServices: [
            account_subnet_api_service,
            ...queryResponse.getAccountSubnetApiServices,
          ],
        },
      })
    },
  })

  const createAccountSubnetApiService = async (input: { subnet_api_service_id: string }) => {
    try {
      const {
        data: { createAccountSubnetApiService },
      } = await mutation({
        variables: {
          input: input,
        },
      })

      return createAccountSubnetApiService
    } catch (error) {
      setToast({
        message: error?.message ?? 'Error creating Account Subnet Api Service',
        type: 'negative',
        open: true,
      })
      throw error // Rethrow the error to propagate it to the caller
    }
  }

  return { createAccountSubnetApiService, loading }
}

export const useGetAccountSubnetApiServices = () => {
  const { data, error, loading, refetch } = useQuery(getAccountSubnetApiServicesGql, {
    fetchPolicy: 'cache-first',
  })

  return {
    data: data?.getAccountSubnetApiServices || [],
    error,
    loading,
    refetch,
  }
}

/**
 * Handles the deletion of an account subnet API service.
 *
 * @param {string} account_subnet_api_service_id - The ID of the account subnet API service to delete.
 * @return {Object} The result of the deletion operation.
 */
export const useDeleteAccountSubnetApiService = () => {
  const { setToast } = useContext(ToastContext)
  const [mutation, { loading }] = useMutation(deleteAccountSubnetApiServiceGql, {
    update(
      cache,
      {
        data: {
          deleteAccountSubnetApiService: { account_subnet_api_service },
        },
      },
    ) {
      cache.modify({
        fields: {
          getTemplates(existingAccountSubnetApi = [], { readField }) {
            return existingAccountSubnetApi.filter(
              (accountSubnetApiRef: any) =>
                account_subnet_api_service.id !== readField('id', accountSubnetApiRef),
            )
          },
        },
      })
    },
  })

  const deleteAccountSubnetApiService = async (account_subnet_api_service_id: string) => {
    try {
      const {
        data: { deleteAccountSubnetApiService },
      } = await mutation({
        variables: {
          account_subnet_api_service_id,
        },
      })

      return deleteAccountSubnetApiService
    } catch (error) {
      setToast({
        message: error?.message ?? 'Error Deleting Account Subnet Api Service',
        type: 'negative',
        open: true,
      })
      throw error // Rethrow the error to propagate it to the caller
    }
  }

  return { deleteAccountSubnetApiService, loading }
}

export const useUpdateSubnetApiKey = () => {
  const { setToast } = useContext(ToastContext)
  const [mutation, { loading }] = useMutation(updateSubnetApiKeyGql)

  const updateSubnetApiKey = async (
    account_subnet_api_service_id: string,
    input: { api_key_id: string | null },
  ) => {
    try {
      const {
        data: { updateSubnetApiKey },
      } = await mutation({
        variables: {
          account_subnet_api_service_id,
          input,
        },
      })

      return updateSubnetApiKey
    } catch (error) {
      setToast({
        message: error?.message ?? 'Error Updating Account Subnet Api Key',
        type: 'negative',
        open: true,
      })
      throw error // Rethrow the error to propagate it to the caller
    }
  }

  return { updateSubnetApiKey, loading }
}
